import React, { useContext, useEffect, useState, memo } from 'react';
import PropTypes from 'prop-types';

import {
    Form,
    InputGroup,
    Badge
} from 'react-bootstrap';
import Tooltip from '@sumologic/yama-ds-components/components/Tooltip';

import DOMPurify from 'dompurify';

import { TEXTAREA, MULTILIST } from '~/modules/customFieldTypes';
import { RE } from '~/modules/helpers/regexp';

import Translate from '~/components/Translate/Translate';

import FormField from '~/containers/FormField/FormField';

import RunbookCanvasContext from '~/containers/RunbookCanvas/RunbookCanvasContext';
import { getPlaceholderString } from '~/containers/RunbookCanvas/Graph/getPlaceholderString';
import { previousActionValueButtons } from './ModalBody';

const ActionFields = ({
    section,
    fields,
    setFieldValue,
    formProps = {},
    disableChange,
    validateOnFieldsChange,
    previousActionButtonFunction,
    onTextAreaReady,
}) => {
    const runbookContext = useContext(RunbookCanvasContext);

    const [formFields, setFormFields] = useState(fields);


    useEffect(() => {
        setFormFields(fields);
        validateOnFieldsChange && formProps?.validateForm();
    }, [fields]);

    return (
        <div className="action-fields">
            {formFields?.map(field => {
                const fieldId = field.id;
                const fieldHint = field.hint || '';

                field.isTouched = formProps.touched?.[fieldId];

                let feedback = null;
                const fieldErrors = formProps.errors?.[fieldId];
                field.errors = fieldErrors;
                if (fieldErrors) {
                    feedback = (
                        <Form.Control.Feedback type="invalid">
                            {fieldErrors}
                        </Form.Control.Feedback>
                    );
                }

                const fieldLabel = field.hideLabel
                    ? ''
                    : field.label || field.name;

                const isNotTextareaType = TEXTAREA !== field.type;
                const fieldWrapperStyle = isNotTextareaType
                    ? null
                    : { display: 'block' };

                let fieldValue = field.value?.value;
                if ('' === fieldValue || undefined === fieldValue) {
                    fieldValue = field.value;
                }

                let formField = null;
                if (fieldValue) {
                    if ('string' === typeof fieldValue) {
                        const regexp = RE.PLAYBOOK.placeholder;
                        if (MULTILIST === field.type) {
                            let someFlowOrPlaybook = false;

                            fieldValue = fieldValue.split(',').map(str => {
                                if (!someFlowOrPlaybook) {
                                    someFlowOrPlaybook = regexp.flowOrPlaybook.test(
                                        str
                                    );
                                }
                                return {
                                    value: str,
                                    label: str
                                };
                            });

                            field.value = someFlowOrPlaybook
                                ? fieldValue.map(item => {
                                      const { label, value } = item;
                                      if (runbookContext) {
                                          //FM: show placeholder box in edit modal for multilist value
                                          const actionId = label.split('::')[1];
                                          return (actionId &&
                                              runbookContext.getNodeByActionId(
                                                  actionId
                                              )) ||
                                              RE.playbook.test(label)
                                              ? {
                                                    value: value,
                                                    label: getPlaceholderString(
                                                        label,
                                                        runbookContext
                                                    )
                                                }
                                              : item;
                                      } else {
                                          return {
                                              value: value,
                                              label:
                                                  'Value from previous action'
                                          };
                                      }
                                  })
                                : fieldValue;
                        } else if(field.type !== TEXTAREA){
                            if (
                                regexp.flowOrPlaybookOrPlaybookPayload.test(
                                    fieldValue
                                )
                            ) {
                                //sst: show placeholder box in edit modal (home action card)
                                if (runbookContext) {
                                    //sst: only if node exists or is a input value (nested)
                                    const actionId = fieldValue.split('::')[1];
                                    if (
                                        (actionId &&
                                            runbookContext.getNodeByActionId(
                                                actionId
                                            )) ||
                                        regexp.playbookOrPlaybookPayload.test(
                                            fieldValue
                                        )
                                    ) {
                                        formField = (
                                            <>
                                                {getPlaceholderString(
                                                    fieldValue,
                                                    runbookContext
                                                )}
                                            </>
                                        );
                                    }
                                } else {
                                    formField = (
                                        <div className="placeholder-value">
                                            <Translate message="Value from previous action" />
                                        </div>
                                    );
                                }
                            }
                        }
                    }
                }

                if (!formField) {
                    const fieldPlaceholder = field?.placeholder;
                    if (
                        fieldPlaceholder &&
                        '__hash__' !== fieldPlaceholder &&
                        Array.isArray(fieldPlaceholder)
                    ) {
                        formField = (
                            <InputGroup className="form-control overflow-auto pt-1 pb-0 background-15">
                                {fieldPlaceholder.map((item, index) => (
                                    <Badge
                                        key={index}
                                        bg="primary"
                                        className="me-1 mt-0 mb-1 ms-0"
                                    >
                                        {item}
                                    </Badge>
                                ))}
                            </InputGroup>
                        );
                    } else {
                        formField = (
                            <FormField {...field} onChange={setFieldValue} onTextAreaReady={onTextAreaReady} />
                        );
                        if (
                            isNotTextareaType &&
                            fieldValue?.length > 25 // 𝘮𝘪𝘯𝘪𝘮𝘶𝘮
                        ) {
                            formField = (
                                <Tooltip
                                    title={fieldValue}
                                    placement="bottom"
                                    arrow
                                >
                                    {formField}
                                </Tooltip>
                            );
                        }
                    }
                }

                return (
                    <Form.Group
                        key={fieldId}
                        className="form-group"
                        //controlId={field.id} RN: TODO check if some behavior change without controlID
                    >
                        {fieldLabel && (
                            <Form.Label
                                required={field.isRequired || field.required}
                            >
                                {fieldHint ? (
                                    <Tooltip
                                        title={
                                            <span
                                                dangerouslySetInnerHTML={{
                                                    __html: DOMPurify.sanitize(fieldHint)
                                                }}
                                            />
                                        }
                                        placement="top"
                                        arrow
                                    >
                                        <span>{fieldLabel}</span>
                                    </Tooltip>
                                ) : (
                                    <span>{fieldLabel}</span>
                                )}
                            </Form.Label>
                        )}

                        <InputGroup style={fieldWrapperStyle}>
                            {formField}
                            {('runbook' === section ||
                                field?.showAddPlaceholderButton) &&
                                previousActionValueButtons(
                                    field,
                                    disableChange,
                                    formProps,
                                    previousActionButtonFunction
                                )}
                            {feedback}
                        </InputGroup>

                        {fieldHint && (
                            <Form.Text className="text-muted text-break">
                                {fieldHint}
                            </Form.Text>
                        )}
                    </Form.Group>
                );
            })}
        </div>
    );
};

ActionFields.propTypes = {
    fields: PropTypes.array.isRequired,
    formProps: PropTypes.object.isRequired,
    setFieldValue: PropTypes.func.isRequired,
    disableChange: PropTypes.bool,
    validateOnFieldsChange: PropTypes.bool,
    previousActionButtonFunction: PropTypes.func
};

export default memo(ActionFields);
